/**
 * Height of the site header's inner bar.
 *
 * Shared because the header is `position: sticky` at the top of the viewport, so
 * anything else that pins itself has to clear it. Kept here rather than
 * duplicated as a literal, since a header that changes height without its
 * followers knowing produces overlaps that are easy to miss.
 */
export const HEADER_HEIGHT = 56;

/**
 * Height of the breadcrumb row — the preset page's, and `BreadcrumbWrap`'s on
 * every other page, so the trail sits at the same height everywhere.
 *
 * Fixed, not intrinsic, because the sticky left rail beneath it is sized as
 * `100vh` minus everything above — and "everything above" has to be a number
 * both sides agree on. An intrinsic row that wrapped to two lines on a long
 * trail would silently push the rail's Generate button off-screen.
 */
export const BREADCRUMB_HEIGHT = 48;

/**
 * The preset page's outer gutter — the margin around its two panels on every
 * side, and the gap between them.
 *
 * Deliberately tiny: both columns are bordered cards, so the border marks where
 * each one ends. Space spent outside them buys nothing and costs the catalogue
 * a masonry column. `genai-studio-v2` separates its desktop panels the same way.
 *
 * Shared, because the top gutter is part of the distance between the viewport
 * top and the panels — anything that pins itself there has to include it. A
 * sticky element whose `top` omits it starts *below* where it will settle and
 * visibly slides up on the first pixels of scroll before it catches.
 */
export const PAGE_GUTTER = 2;

/**
 * The same gutter, horizontally.
 *
 * Wider than `PAGE_GUTTER` because the two edges are not equivalent: vertical
 * space is scarce — it comes straight off the panels' height and pushes Generate
 * toward the fold — while a few pixels at the sides only stop the cards sitting
 * flush against the window, which reads as clipped.
 *
 * Safe to change on its own: nothing sticky measures against it, unlike
 * `PAGE_GUTTER`.
 */
export const PAGE_GUTTER_X = 12;

/**
 * The gutter below the panels.
 *
 * Its own value rather than reusing `PAGE_GUTTER`: the top gutter is what the
 * panels' sticky `top` is measured against, so it has to stay minimal, while the
 * bottom is free to breathe. Both are subtracted from the panels' height.
 */
export const PAGE_GUTTER_BOTTOM = 8;

/**
 * NOTE: the preset page used to paint itself pure black from SM up while its
 * panels, header and sticky bands used `BackgroundColors.base` — a near-black
 * grey. Those two are close enough to look like a mistake and far enough apart
 * to be visible, so every opaque overlay read as a lighter slab.
 *
 * There is now one surface colour, `BackgroundColors.base`, and separation comes
 * from borders instead. Nothing should reintroduce a second page background.
 */

/**
 * The site's horizontal page gutter — the distance from the viewport edge to
 * the first column of content, on the header and on every page body.
 *
 * One value, because six stylesheets were each carrying their own copy of it
 * and had already drifted to three different numbers. The full-bleed rails
 * (`SectionSlider`, `TrendingSection`) are the reason that matters: they cancel
 * the gutter with a negative margin and re-apply it inside, so their first card
 * lands back on the page's column. Any disagreement between the two numbers is
 * a visible misalignment, and it is the kind that reads as a bug rather than a
 * choice.
 *
 * Plain numbers, not `Spacings.sN`. Those tokens are CSS custom properties, and
 * a variable cannot be negated by string interpolation — `` `-${Spacings.s44}` ``
 * produces `-var(--spacings-s44)`, which is invalid CSS and is dropped
 * silently. That is exactly how the rails drifted 32px out of line. A number
 * negates.
 */
export const SITE_GUTTER = { mobile: 12, desktop: 16 } as const;
